// The only place in the codebase allowed to write og:*/twitter:* tags.
//
// Every page used to hand-roll its own version of this block. Five ended up
// concatenating an unguarded image import and shipping
// "https://www.pedalsure.comundefined"; several never got an image at all;
// one copy-pasted another page's og:title and never changed it. All three
// are the same root cause - no single function owned this - so this is that
// function: it cannot emit a broken image, and it never takes a title as a
// second, separately-typed string.
import { SITE_URL, getCanonicalUrl } from '@/helpers/seo'

export const FALLBACK_OG_IMAGE = `${SITE_URL}/images/og/pedalsure-default-1200x630.png`

// Rejects anything that would end up in a broken tag - unset, empty, or the
// literal string 'undefined'/'null' (what an unguarded template-string
// concatenation produces on these exact five pages) - and joins anything
// that isn't already absolute onto SITE_URL rather than emitting it relative.
export function resolveSocialImage(raw, baseUrl = SITE_URL) {
  if (!raw || raw === 'undefined' || raw === 'null') {
    return FALLBACK_OG_IMAGE
  }

  if (/^https?:\/\//i.test(raw)) {
    return raw
  }

  const base = String(baseUrl).replace(/\/+$/, '')
  const rel = String(raw).replace(/^\/+/, '')
  return `${base}/${rel}`
}

// `title`/`description` must be the same values the page already uses for
// its <title>/meta description - never retyped - so og:title cannot drift
// from the page the way /road-bike-insurance's did.
export function buildSocialMeta({
  title,
  description,
  path,
  image,
  type = 'website',
  imageAlt,
}) {
  const resolvedImage = resolveSocialImage(image)
  const url = getCanonicalUrl(path)
  const alt = imageAlt || title

  return {
    meta: [
      { hid: 'og:site_name', property: 'og:site_name', content: 'Pedalsure' },
      { hid: 'og:type', property: 'og:type', content: type },
      { hid: 'og:url', property: 'og:url', content: url },
      { hid: 'og:title', property: 'og:title', content: title },
      {
        hid: 'og:description',
        property: 'og:description',
        content: description,
      },
      { hid: 'og:image', property: 'og:image', content: resolvedImage },
      { hid: 'og:image:width', property: 'og:image:width', content: '1200' },
      { hid: 'og:image:height', property: 'og:image:height', content: '630' },
      { hid: 'og:image:alt', property: 'og:image:alt', content: alt },
      {
        hid: 'twitter:card',
        name: 'twitter:card',
        content: 'summary_large_image',
      },
      { hid: 'twitter:title', name: 'twitter:title', content: title },
      {
        hid: 'twitter:description',
        name: 'twitter:description',
        content: description,
      },
      { hid: 'twitter:image', name: 'twitter:image', content: resolvedImage },
      { hid: 'twitter:url', name: 'twitter:url', content: url },
    ],
  }
}
